import React, { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import {
  destroyMember,
  destroyMemberMfa,
  inviteMembers,
  passwordReset,
  updateMember,
} from '../../../../states/containers/member/memberSlice'
import { format, nowDate } from '../../../../utils/date'
import { useTranslation } from 'react-i18next'
import { sortBy, uniqueId } from 'lodash'
import MemberModal from '../../../components/setting/member/MemberModal'
import ConfirmationModal from '../../../components/setting/member/ConfirmationModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Member } from '../../../../states/types/member'
import Tips from '../../../components/common/Tips'
import { fetchTeamList } from '../../../../states/containers/team/listSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import VideoGuide from '../../../components/common/VideoGuide'
import InvitationModal from '../../../components/setting/member/InvitationModal'
import { fetchHolidaySchedule } from '../../../../states/containers/holidaySchedule/holidayScheduleSlice'
import { compile } from '../../../../utils/search'
import ExtraOrderModal from '../../../components/common/ExtraOrderModal'
import { isAdministrator } from '../../../../utils/role'
import CsvModule from '../../../components/setting/CsvModule'

interface InvitationModalState {
  display: boolean,
  targetMemberList: Member[]
}

interface MemberModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetMemberData: Member | null
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const DISPLAY_BLOCK_NUMBER = 15

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const FORMAT_DATETIME = t('time.date.formats.with_time')

  const {
    commonPart,
    memberList,
    login,
  } = useSelector((state: RootState) => (state))

  const [isCloseable, setIsCloseable] = useState<boolean>(true)
  const [searchWord, setSearchWord] = useState<string>("")
  const [members, setMembers] = useState<Member[]>()
  const [membersInit, setMembersInit] = useState<Member[]>()

  const [sortType, setSortType] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [isAllSearch, setIsAllSearch] = useState<boolean>(false)

  const [composition, setComposition] = useState<boolean>(false)

  const [invitationModalState, setInvitationModalState] = useState<InvitationModalState>({
    display: false,
    targetMemberList: [],
  })

  const [memberModalState, setMemberModalState] = useState<MemberModalState>({
    display: false,
    type: "",
    id: undefined,
    targetMemberData: null,
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [displayExtraOrder, setDisplayExtraOrder] = useState<boolean>(false)

  const [fetchTrigger, setFetchTrigger] = useState<string>("")

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && (members?.length || 0) >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, members])

  const unInstallCount = useMemo(() => (
    memberList.members.reduce((s, v) => (!v.last_created_at ? s + 1 : s), 0)
  ), [memberList])

  const openInvitationModal = () => {
    setInvitationModalState({
      display: true,
      targetMemberList: membersInit?.filter((v) => !v.current_sign_in_at && v.active) || [],
    })
  }

  const closeInvitationModal = () => {
    setInvitationModalState({
      display: false,
      targetMemberList: [],
    })
  }

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    setIsCloseable(!commonPart.is_first_manager)
  }, [login.accessToken, commonPart.company])

  const closeMemberModal = () => {
    setMemberModalState({
      ...memberModalState,
      targetMemberData: null,
      display: false,
    })
  }

  const addMemberCallback = () => {
    setSearchWord("")
    setIsAllSearch(true)
    setFetchTrigger(uniqueId())
  }

  const modMemberCallback = () => {
    setFetchTrigger(uniqueId())
  }

  const deleteMember = (memberData: Member) => {
    if (!memberData.id) return
    dispatch(
      destroyMember(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.delete_member', { name: memberData.name }))
          closeMemberModal()
          setFetchTrigger(uniqueId())
        },
      ),
    )
  }

  const resetMember = (memberData: Member) => {
    if (!memberData.id) return
    dispatch(
      passwordReset(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.reset_password', { name: memberData.name }))
          closeMemberModal()
          setFetchTrigger(uniqueId())
        },
      ),
    )
  }

  const disableMemberMfa = (memberData: Member) => {
    if (!memberData.id) return
    dispatch(
      destroyMemberMfa(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.delete_mfa', { name: memberData.name }))
          closeMemberModal()
          if (!commonPart.company?.id) return
          dispatch(
            fetchMemberList(),
          )
        },
      ),
    )
  }

  const activateMember = (memberData: Member, active: boolean, is_mail?: boolean) => {
    dispatch(
      updateMember(
        memberData.id,
        { active: active },
        () => {
          alert.success(`${memberData.name}さんに${active ? "復帰" : "休眠"}処理をおこないました`)
          if (is_mail) {
            dispatch(
              passwordReset(
                memberData.id,
              ),
            )
          }
          setFetchTrigger(uniqueId())
        },
        () => {
          alert.error('エラーが発生しました。管理者にお問い合わせください。')
        },
      ),
    )
  }

  // confirmation modal
  const openConfirmationModal = (memberData: Member, type: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let callback = (is_mail?: boolean) => {
      // none
    }
    switch (type) {
      case "reset":
        callback = () => resetMember(memberData)
        break
      case "delete":
        callback = () => deleteMember(memberData)
        break
      case "disable":
        callback = () => disableMemberMfa(memberData)
        break
      case "active":
        callback = (is_mail?: boolean) => activateMember(memberData, true, is_mail)
        break
      case "inactive":
        callback = () => activateMember(memberData, false)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: memberData.name,
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openMemberModal = (memberData: Member | null, type: string) => {
    setMemberModalState({
      ...memberModalState,
      display: true,
      type: type,
      id: memberData?.id || 0,
      targetMemberData: memberData,
    })
  }

  const checkLatestVersion = (current: string, platform: string):boolean => {
    let latest
    if (platform === "Windows") {
      latest = memberList.latest_versions.windows
    } else {
      latest = memberList.latest_versions.macos
    }
    const current_num = current.split('.').map((value) => (`000${value}`).slice(-3)).join('')
    const latest_num = latest.split('.').map((value) => (`000${value}`).slice(-3)).join('')
    if (Number(current_num) < Number(latest_num)) return false
    return true
  }

  const reset = () => {
    setSearchWord("")
    setSortType("")
    setMembers(membersInit)
    setDisplayBlockNum(1)
  }

  const search = (originalList: Member[] | undefined) => {
    setSortType("")
    if (!searchWord) {
      reset()
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name + member.contract + member.place + member.email + member.app_version + member.platform + member.kana))
    }) as Member[]
    setMembers(sortBy(list, 'id'))
  }

  const sortMembers = (key: string, type: string) => {
    const tmpList = sortBy(members, key)
    if (type === "desc") {
      return setMembers(tmpList.reverse())
    }
    return setMembers(tmpList)
  }

  const sortBlock = (label: string, type: string) => (
    <>
      {
        sortType === `${type}_-_desc` && (
          <div
            className="sort_block"
            onClick={() => setSortType(`${type}_-_asc`)}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort-amount-down']} />
            </span>
          </div>
        )
      }
      {
        sortType === `${type}_-_asc` && (
          <div
            className="sort_block"
            onClick={() => setSortType("")}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort-amount-down-alt']} />
            </span>
          </div>
        )
      }
      {
        !sortType.includes(type) && (
          <div
            className="sort_block"
            onClick={() => setSortType(`${type}_-_desc`)}
            role="none"
          >
            {label}
            <span
              className="sort_button"
            >
              <FontAwesomeIcon icon={['fas', 'sort']} />
            </span>
          </div>
        )
      }
    </>
  )

  const sendInvitation = (memberIds: number[]) => {
    dispatch(
      inviteMembers(
        { member_ids: memberIds },
        () => alert.success(`${memberIds.length}件の招待メールを送信しました。`),
      ),
    )
    closeInvitationModal()
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const reload = () => {
    if (!commonPart.company?.id) return
    setIsLoading(true)
    dispatch(
      fetchMemberList(() => {
        setFetchTrigger(uniqueId())
      }),
    )
  }

  useEffect(() => {
    if (isLoading) return
    if (!sortType) {
      setMembers(sortBy(members, 'id'))
      return
    }
    const [key, type] = sortType.split("_-_")
    sortMembers(key, type)
  }, [sortType])

  useEffect(() => {
    if (!fetchTrigger) return
    if (!memberList.members) return
    if (!searchWord || isAllSearch) {
      setMembers(sortBy(memberList.members, 'id'))
      setIsAllSearch(false)
    } else {
      search(memberList.members)
    }
    setMembersInit(sortBy(memberList.members, 'id'))
  }, [fetchTrigger, memberList.members])

  useEffect(() => {
    dispatch(
      fetchMemberList(() => {
        setFetchTrigger(uniqueId())
      }),
    )
    dispatch(
      fetchTeamList(() => { /* do nothing */ }),
    )
    dispatch(fetchHolidaySchedule())
  }, [])

  useEffect(() => {
    if (memberList.isLoading || !fetchTrigger) return
    setFetchTrigger("")
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [members])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t('setting.member.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.member.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.member.title')}
          <VideoGuide
            videoId="bBeorfheDjU"
          />
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder={t('member.list.search.placeholder')}
              onChange={(event) => setSearchWord(event.target.value)}
              onBlur={() => search(membersInit)}
              onKeyDown={(event) => keyDown(event)}
              onCompositionStart={() => setComposition(true)}
              onCompositionEnd={() => setComposition(false)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
          {
            !!membersInit?.filter((v) => !v.current_sign_in_at && v.active).length && (
              <div className="button_area">
                <button
                  type="button"
                  className="button"
                  onClick={() => openInvitationModal()}
                >
                  <FontAwesomeIcon icon={['fas', 'paper-plane']} />
                  招待メール一括送信
                </button>
              </div>
            )
          }
          <CsvModule
            title={
              `メンバー一覧 ${nowDate("YYYYMMDD")}`
            }
            labels={{
              member_name: "名前",
              member_kana: "かな",
              member_code: "コード",
              mail_address: "メールアドレス",
              qasee_client_install: "QaseeClientインストール",
              os: "QaseeClient利用OS",
              qasee_version: "QaseeClientバージョン",
              latest_use_date: "最終データ収集日時",
              mfa: "多要素認証利用",
              active: "現在の状態",
            }}
            list={
              members?.map((v) => (
                {
                  member_name: v.name,
                  member_kana: v.kana,
                  member_code: v.code,
                  mail_address: v.email,
                  qasee_client_install: v.last_created_at ? "インストール済み" : "未インストール",
                  os: v.platform || "-",
                  qasee_version: v.app_version || "-",
                  latest_use_date: v.last_created_at || "-",
                  mfa: v.mfa_flg ? "利用あり" : "利用なし",
                  active: v.active ? "通常" : "休眠",
                }
              )) || []
            }
          />
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              {t('setting.member.caption')}
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "4px",
              }}
            >
              <div className="member_summary">
                <div>
                  現在の総数
                  <span className="active_count">
                    {memberList.members.filter((v) => !v.is_support).length}
                  </span>
                  名(休眠
                  <span className="inactive_count">
                    {memberList.members.filter((v) => !v.active).length}
                  </span>
                  名を含む)
                </div>
                <div className="separator">
                  /
                </div>
                <div>
                  上限数
                  <span className="limit_count">
                    {memberList.limit_member_number - memberList.members.filter((v) => v.is_support).length}
                  </span>
                  名
                </div>
              </div>
              {
                !!unInstallCount && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    QaseeClient 未インストールメンバー数
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#e44242",
                      }}
                    >
                      {unInstallCount}
                    </div>
                    <div>
                      /
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      {memberList.members.filter((v) => !v.is_support).length}
                    </div>
                    名
                  </div>
                )
              }
            </div>
            <div className="button_area">
              {
                (isAdministrator(commonPart.role) && login.change_contract) && (
                  <button
                    type="button"
                    className="change_contract"
                    onClick={() => setDisplayExtraOrder(true)}
                  >
                    <FontAwesomeIcon icon={['far', 'lightbulb']} />
                    メンバー上限の開放
                  </button>
                )
              }
              <button
                type="button"
                className="reload"
                onClick={reload}
                disabled={isLoading}
              >
                <Tips
                  description="最新の情報に更新"
                  className=""
                  icon={false}
                >
                  <>
                    <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                  </>
                </Tips>
              </button>
              {
                memberList.limit_member_number <= memberList.members.length ? (
                  <Tips
                    description="メンバー数が上限に達している為、追加できません"
                    className=""
                    icon={false}
                  >
                    <button
                      type="button"
                      className="button add_one_member disabled"
                    >
                      <FontAwesomeIcon icon={['fas', 'plus']} />
                      {t('setting.member.add_member')}
                    </button>
                  </Tips>
                ) : (
                  <button
                    type="button"
                    className="button add_one_member"
                    onClick={() => openMemberModal(null, "create")}
                  >
                    <FontAwesomeIcon icon={['fas', 'plus']} />
                    {t('setting.member.add_member')}
                  </button>
                )
              }
            </div>
          </div>
          {
            isLoading ? (
              <LoadingSquare>
                <>
                  メンバー情報を取得しています…
                </>
              </LoadingSquare>
            ) : (
              <div className="block-detail">
                <table className="flex-table member_list_table">
                  <thead>
                    <tr>
                      <th>操作</th>
                      <th>
                        {sortBlock(t('model.member.name'), "kana")}
                      </th>
                      <th>
                        {sortBlock(t('model.member.email'), "email")}
                      </th>
                      <th>
                        {sortBlock(t('model.member_detail.app_version'), "app_version")}
                      </th>
                      <th>
                        {sortBlock(t('model.member.last_created_at'), "last_created_at")}
                      </th>
                      <th>{t('model.member.mfa')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      members && members.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((memberData: Member) => (
                        <tr key={`${memberData.email}_${memberData.name}`} className={memberData.active ? "" : "inactive"}>
                          <td>
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div>
                                <Tips
                                  description="メンバーを編集"
                                  className=""
                                  icon={false}
                                >
                                  <button
                                    type="button"
                                    onClick={() => openMemberModal(memberData, "update")}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'tools']} />
                                  </button>
                                </Tips>
                              </div>
                              {
                                memberData.active && (
                                  <>
                                    <div>
                                      <Tips
                                        description="パスワードをリセット"
                                        className=""
                                        icon={false}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => openConfirmationModal(memberData, "reset")}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'key']} />
                                        </button>
                                      </Tips>
                                    </div>
                                  </>
                                )
                              }
                              {
                                memberData.active && memberData.mfa_flg && (
                                  <div>
                                    <Tips
                                      description="多要素認証を解除"
                                      className=""
                                      icon={false}
                                    >
                                      <button
                                        type="button"
                                        className="del"
                                        onClick={() => openConfirmationModal(memberData, "disable")}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'exclamation-circle']} />
                                      </button>
                                    </Tips>
                                  </div>
                                )
                              }
                              {
                                !memberData.is_master && memberData.id !== login.current_member_id && (
                                  <>
                                    {
                                      memberData.is_support || (
                                        <div>
                                          {
                                            memberData.active ? (
                                              <>
                                                <Tips
                                                  description="アカウントを休眠する"
                                                  className=""
                                                  icon={false}
                                                  key={`inactive_${memberData.email}`}
                                                >
                                                  <button
                                                    className="del"
                                                    type="button"
                                                    onClick={() => openConfirmationModal(memberData, "inactive")}
                                                  >
                                                    <FontAwesomeIcon icon={['far', 'pause-circle']} />
                                                  </button>
                                                </Tips>
                                              </>
                                            ) : (
                                              <>
                                                <Tips
                                                  description="アカウントを復帰する"
                                                  className=""
                                                  icon={false}
                                                  key={`active_${memberData.email}`}
                                                >
                                                  <button
                                                    className="green"
                                                    type="button"
                                                    onClick={() => openConfirmationModal(memberData, "active")}
                                                  >
                                                    <FontAwesomeIcon icon={['far', 'play-circle']} />
                                                  </button>
                                                </Tips>
                                              </>
                                            )
                                          }
                                        </div>
                                      )
                                    }
                                    <div>
                                      <Tips
                                        description="メンバーを削除"
                                        className=""
                                        icon={false}
                                      >
                                        <button
                                          type="button"
                                          className="del"
                                          onClick={() => openConfirmationModal(memberData, "delete")}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                        </button>
                                      </Tips>
                                    </div>
                                  </>
                                )
                              }
                            </div>
                          </td>
                          <td className="no_trans">
                            {
                              memberData.profile_image ? (
                                <img alt="profile" src={memberData.profile_image} className="profile_icon" />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(memberData.id) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                />
                              )
                            }
                            {
                              memberData.active ? (
                                <>
                                  <ruby>
                                    {memberData.name}
                                    <rt>{memberData.kana}</rt>
                                  </ruby>
                                  {
                                    memberData.code && (
                                      <>
                                        {`(${memberData.code})`}
                                      </>
                                    )
                                  }
                                  {
                                    memberData.connect_id && (
                                      <>
                                        <Tips
                                          description="Freee人事労務と連携中"
                                          className=""
                                          icon={false}
                                          style={{
                                            color: "#0f0dca",
                                          }}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'link']} />
                                        </Tips>
                                      </>
                                    )
                                  }
                                </>
                              ) : (
                                <Tips
                                  description="休眠中💤"
                                  className=""
                                  icon={false}
                                >
                                  <>
                                    <ruby>
                                      {memberData.name}
                                      <rt>{memberData.kana}</rt>
                                    </ruby>
                                    {
                                      memberData.code && (
                                        <>
                                          {`(${memberData.code})`}
                                        </>
                                      )
                                    }
                                    💤
                                  </>
                                </Tips>
                              )
                            }
                          </td>
                          <td className="no_trans">{memberData.email}</td>
                          <td>
                            {
                              memberData.app_version && memberData.platform ? (
                                <>
                                  {
                                    checkLatestVersion(memberData.app_version, memberData.platform) ? (
                                      <>
                                        {`${memberData.app_version} (${memberData.platform})`}
                                      </>
                                    ) : (
                                      <Tips
                                        description="QaseeClientのバージョンアップをおこなってください。"
                                        className=""
                                        icon={false}
                                      >
                                        <>
                                          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ color: "#E44242" }} />
                                          &nbsp;
                                          {`${memberData.app_version} (${memberData.platform})`}
                                        </>
                                      </Tips>
                                    )
                                  }
                                </>
                              ) : (
                                "-"
                              )
                            }
                          </td>
                          <td>
                            {
                              memberData.last_created_at ? (
                                format(memberData.last_created_at, FORMAT_DATETIME)
                              ) : (
                                <span style={{ color: "#FF0000", fontWeight: "bold" }}>
                                  未インストール
                                </span>
                              )
                            }
                          </td>
                          <td>
                            {
                              memberData.mfa_flg ? (
                                <span className="green">
                                  {t('setting.member.enable')}
                                </span>
                              ) : (
                                <span>
                                  {t('setting.member.disable')}
                                </span>
                              )
                            }
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
        {/* 編集モーダル */}
        <MemberModal
          memberModalState={memberModalState}
          closeCallback={closeMemberModal}
          addCallback={addMemberCallback}
          modCallback={modMemberCallback}
          closeable={isCloseable}
        />
        {/* 確認モーダル */}
        <ConfirmationModal
          confirmationModalState={confirmationModalState}
          closeCallback={closeConfirmationModal}
        />
        {/* 一括招待モーダル */}
        <InvitationModal
          invitationModalState={invitationModalState}
          invitationCallback={(memberIds) => sendInvitation(memberIds)}
          closeCallback={closeInvitationModal}
          closeable
        />
        <ExtraOrderModal
          display={displayExtraOrder}
          closeCallback={() => setDisplayExtraOrder(false)}
        />
      </section>
    </section>
  )
}

export default Index
